import { useEffect, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Pencil, Search, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { api, unwrap } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Input, Label } from '@/components/ui/Input';
import { Avatar, AvatarFallback } from '@/components/ui/Avatar';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/Dialog';

interface UserItem {
  id: number;
  name: string;
  email: string;
  role: string | null;
  created_at: string;
}

interface Paginated<T> {
  data: T[];
  total: number;
  current_page: number;
  last_page: number;
}

export const Route = createFileRoute('/_app/users/')({
  component: UsersPage,
});

function UsersPage() {
  const qc = useQueryClient();
  const { user: me } = useAuth();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<UserItem | null>(null);
  const [creating, setCreating] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['users', q, page],
    queryFn: () => {
      const params = new URLSearchParams({ per_page: '25', page: String(page) });
      if (q) params.set('q', q);
      return unwrap<Paginated<UserItem>>(api.get(`/users?${params}`));
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/users/${id}`),
    onSuccess: () => {
      toast.success('Usuario eliminado');
      void qc.invalidateQueries({ queryKey: ['users'] });
    },
    onError: () => toast.error('No se pudo eliminar'),
  });

  return (
    <div className="mx-auto max-w-7xl space-y-6 p-6 md:p-8">
      <header className="flex flex-col gap-3 md:flex-row md:items-end md:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Usuarios</h1>
          <p className="text-sm text-neutral-500">Administradores y editores del CMS.</p>
        </div>
        <Button onClick={() => setCreating(true)}>
          <Plus className="size-4" /> Nuevo usuario
        </Button>
      </header>

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <div>
              <CardTitle>Listado</CardTitle>
              <CardDescription>{data?.total ?? 0} usuarios.</CardDescription>
            </div>
            <div className="relative w-64">
              <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-neutral-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar…" className="pl-9" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead className="border-y border-neutral-200 bg-neutral-50 text-left text-xs uppercase tracking-wider text-neutral-500">
              <tr>
                <th className="px-4 py-2">Usuario</th>
                <th className="px-4 py-2">Email</th>
                <th className="px-4 py-2">Rol</th>
                <th className="px-4 py-2">Alta</th>
                <th className="px-4 py-2 text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {isLoading && (
                <tr><td colSpan={5} className="px-4 py-8 text-center text-neutral-400">Cargando…</td></tr>
              )}
              {!isLoading && data?.data?.length === 0 && (
                <tr><td colSpan={5} className="px-4 py-10 text-center text-sm text-neutral-400">
                  {q ? 'Ningún usuario coincide con la búsqueda.' : 'No hay usuarios todavía.'}
                </td></tr>
              )}
              {data?.data?.map((u) => {
                const initials = u.name.split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase();
                const isSelf = me?.id === u.id;
                return (
                  <tr key={u.id} className="border-b border-neutral-100 hover:bg-neutral-50">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <Avatar><AvatarFallback>{initials}</AvatarFallback></Avatar>
                        <div>
                          <div className="font-medium">{u.name}</div>
                          {isSelf && <div className="text-[10px] text-neutral-400">tú</div>}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-neutral-600">{u.email}</td>
                    <td className="px-4 py-3">
                      {u.role && (
                        <Badge variant={u.role === 'admin' ? 'default' : 'secondary'}>
                          <ShieldCheck className="mr-1 size-3" /> {u.role}
                        </Badge>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-neutral-500">
                      {new Date(u.created_at).toLocaleDateString('es')}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(u)}>
                        <Pencil className="size-3.5" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={isSelf}
                        onClick={() => {
                          if (confirm(`¿Eliminar a ${u.name}?`)) remove.mutate(u.id);
                        }}
                        className="text-red-600 hover:bg-red-50 hover:text-red-700"
                      >
                        <Trash2 className="size-3.5" />
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {data && data.last_page > 1 && (
            <div className="flex items-center justify-between border-t border-neutral-200 px-4 py-3 text-xs text-neutral-500">
              <span>Página {data.current_page} de {data.last_page}</span>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(page - 1)}>Anterior</Button>
                <Button size="sm" variant="outline" disabled={page >= data.last_page} onClick={() => setPage(page + 1)}>Siguiente</Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <UserFormDialog
        open={creating || editing != null}
        user={editing}
        onClose={() => {
          setCreating(false);
          setEditing(null);
        }}
      />
    </div>
  );
}

interface UserFormDialogProps {
  open: boolean;
  user: UserItem | null;
  onClose: () => void;
}

function UserFormDialog({ open, user, onClose }: UserFormDialogProps) {
  const qc = useQueryClient();
  const isEdit = user != null;
  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<'admin' | 'editor'>((user?.role as 'admin' | 'editor') ?? 'editor');

  useEffect(() => {
    if (user) {
      setName(user.name);
      setEmail(user.email);
      setRole((user.role as 'admin' | 'editor') ?? 'editor');
      setPassword('');
    } else if (open) {
      setName('');
      setEmail('');
      setRole('editor');
      setPassword('');
    }
  }, [user, open]);

  const save = useMutation({
    mutationFn: async () => {
      const payload: Record<string, unknown> = { name, email, role };
      if (password) payload.password = password;
      if (isEdit) return api.put(`/users/${user!.id}`, payload);
      return api.post('/users', payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Usuario actualizado' : 'Usuario creado');
      void qc.invalidateQueries({ queryKey: ['users'] });
      onClose();
      setPassword('');
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
      toast.error(msg ?? 'Error al guardar');
    },
  });

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar usuario' : 'Nuevo usuario'}</DialogTitle>
          <DialogDescription>Datos de acceso y rol asignado.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="u-name">Nombre</Label>
            <Input id="u-name" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="u-email">Email</Label>
            <Input id="u-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="u-password">{isEdit ? 'Cambiar contraseña (opcional)' : 'Contraseña'}</Label>
            <Input
              id="u-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={isEdit ? 'Dejar en blanco para no cambiar' : 'Mínimo 8 caracteres'}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="u-role">Rol</Label>
            <select
              id="u-role"
              value={role}
              onChange={(e) => setRole(e.target.value as 'admin' | 'editor')}
              className="flex h-9 w-full rounded-md border border-neutral-200 bg-white px-3 text-sm"
            >
              <option value="editor">Editor (puede editar contenidos)</option>
              <option value="admin">Administrador (acceso total)</option>
            </select>
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear usuario'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
